import ScrollTrigger from 'gsap/ScrollTrigger';

export class ParticleWaveBackground {
  isLoaded = false;
  hasStartedBeforeInit = false;

  // Base (un-displaced) positions of particles
  basePositions = null;

  // Spring state (smoothed positions + velocities for elastic motion)
  currentPositions = null;
  velocities = null;

  // Mouse state in plane coordinates
  mouse = { x: 0, y: 0, isInside: false };

  constructor(containerElement, options = {}) {
    // Default configuration
    this.config = {
      planeWidth: 10,
      planeHeight: 5,
      widthSegments: 120,
      heightSegments: 70,
      particleSize: 0.03,
      waveHeight: 0.5,
      noiseScale: 2,
      animationSpeed: 4000,
      fadePower: 2,
      color: [1, 1, 1], // RGB values ($0$–$1$)
      bgColor: [1, 0.18, 0],
      rotationX: -Math.PI / 2.3,
      rotationY: 0,
      rotationZ: 0,
      cameraPosition: [0, 0, 5],
      cameraRotationX: 0,
      cameraRotationY: 0,
      cameraRotationZ: 0,
      autoStart: true,

      // Hover displacement config
      hoverRadius: 1, // in world units
      hoverStrength: 0.5, // how far particles are pushed away

      // Elastic hover animation (tune these to adjust feel)
      hoverSpringStiffness: 40, // higher = snappier attraction to target
      hoverSpringDamping: 10, // higher = less oscillation

      ...options, // Override with user options
    };

    this.container = containerElement;

    // Bind handlers so we can add/remove listeners cleanly
    this.onMouseMove = this.handleMouseMove.bind(this);
    this.onMouseLeave = this.handleMouseLeave.bind(this);

    ScrollTrigger.create({
      trigger: containerElement,
      start: 'top-=500 bottom',
      once: true,
      onEnter: async () => {
        this.init();
      },
    });
  }

  async init() {
    const [THREE, { SimplexNoise }] = await Promise.all([
      import('three'),
      import('three/addons/math/SimplexNoise.js'),
    ]);

    this.THREE = THREE;
    this.SimplexNoise = SimplexNoise;

    this.setupScene();
    this.createParticles();
    if (this.config.autoStart) {
      this.startAnimation();
    }

    this.resizeObserver = new ResizeObserver(() => this.onResize());
    this.resizeObserver.observe(this.container);

    // Mouse listeners for hover effect (on window, so effect works even outside the parent)
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mouseleave', this.onMouseLeave);

    // Enable debug mode if URL has debug=true
    if (new URLSearchParams(window.location.search).has('debug')) {
      import('./AnimationDebugger.js').then(({ AnimationDebugger }) => {
        this.debugger = new AnimationDebugger(this, this.container);
      });
    }

    this.isLoaded = true;
    if (this.hasStartedBeforeInit) this.startAnimation();
  }

  setupScene() {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;

    this.renderer = new this.THREE.WebGLRenderer({
      antialias: false,
      alpha: true,
    });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.container.appendChild(this.renderer.domElement);

    // Canvas styling
    const canvas = this.renderer.domElement;
    canvas.style.position = 'absolute';
    canvas.style.top = '0';
    canvas.style.left = '0';
    // Keep pointer events off the canvas (events come from window)
    canvas.style.pointerEvents = 'none';

    this.scene = new this.THREE.Scene();
    this.camera = new this.THREE.PerspectiveCamera(
      45,
      width / height,
      0.1,
      1000,
    );
    this.updateCameraPosition();

    this.simplex = new this.SimplexNoise();

    // Raycaster for accurate mouse → world mapping
    this.raycaster = new this.THREE.Raycaster();
    this.ndcMouse = new this.THREE.Vector2();

    // Infinite plane helper aligned with the particles (used for fallback)
    this.interactionPlane = new this.THREE.Plane();
  }

  createParticles() {
    const geometry = new this.THREE.PlaneGeometry(
      this.config.planeWidth,
      this.config.planeHeight,
      this.config.widthSegments,
      this.config.heightSegments,
    );

    const colors = new Float32Array(geometry.attributes.position.count * 3);
    geometry.setAttribute('color', new this.THREE.BufferAttribute(colors, 3));

    this.particles = new this.THREE.Points(
      geometry,
      new this.THREE.PointsMaterial({
        size: this.config.particleSize,
        vertexColors: true,
        transparent: false,
        blending: this.THREE.NormalBlending,
      }),
    );

    this.particles.rotation.set(
      this.config.rotationX,
      this.config.rotationY,
      this.config.rotationZ,
    );
    this.scene.add(this.particles);
    this.particleGeometry = geometry;

    // Store base (original) positions so displacement is non-cumulative
    this.basePositions =
      this.particleGeometry.attributes.position.array.slice();

    // Initialize spring state arrays
    this.currentPositions = new Float32Array(this.basePositions.length);
    this.currentPositions.set(this.basePositions);
    this.velocities = new Float32Array(this.basePositions.length); // starts at $0$

    // Invisible mesh aligned with the particle plane for local transforms
    this.intersectionPlane = new this.THREE.Mesh(
      new this.THREE.PlaneGeometry(
        this.config.planeWidth,
        this.config.planeHeight,
      ),
      new this.THREE.MeshBasicMaterial({ visible: false }),
    );
    this.intersectionPlane.position.copy(this.particles.position);
    this.intersectionPlane.rotation.copy(this.particles.rotation);
    this.scene.add(this.intersectionPlane);
  }

  startAnimation() {
    if (!this.isLoaded) {
      this.hasStartedBeforeInit = true;
      return;
    }

    if (this.animationId) return;

    let lastTime = 0;
    const animate = (time) => {
      this.animationId = requestAnimationFrame(animate);

      // Skip frames when tab isn't visible
      if (document.hidden) {
        lastTime = time;
        return;
      }

      // Throttle to ~30fps
      if (time - lastTime > 33) {
        const delta = (time - lastTime) / 1000; // seconds
        this.updateParticles(time, delta);
        this.renderer.render(this.scene, this.camera);
        lastTime = time;
      }
    };
    animate(0);
  }

  stopAnimation() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  // --- Elastic particle update with spring-like mouse displacement ---
  updateParticles(time, delta) {
    const positions = this.particleGeometry.attributes.position;
    const colors = this.particleGeometry.attributes.color;
    const base = this.basePositions;

    const [r, g, b] = this.config.color;
    const [bgR, bgG, bgB] = this.config.bgColor;

    const mouseActive = this.mouse.isInside && this.config.hoverStrength > 0;
    const radius = this.config.hoverRadius;

    // Cap delta to avoid instability on big frame drops
    const dt = Math.min(delta || 0.016, 0.05);

    const k = this.config.hoverSpringStiffness;
    const d = this.config.hoverSpringDamping;

    for (let i = 0; i < positions.count; i++) {
      const idx = i * 3;

      // Base (un-displaced) position from geometry
      const baseX = base[idx];
      const baseY = base[idx + 1];

      // Start from base as the default target
      let targetX = baseX;
      let targetY = baseY;

      // Mouse-based displacement (repulsion from the cursor)
      if (mouseActive && radius > 0) {
        const dx = baseX - this.mouse.x;
        const dy = baseY - this.mouse.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist > 0 && dist < radius) {
          const falloff = 1 - dist / radius; // $1$ at center → $0$ at radius
          const force = falloff * this.config.hoverStrength;

          targetX += (dx / dist) * force;
          targetY += (dy / dist) * force;
        }
      }

      // Spring dynamics toward targetX / targetY
      const curX = this.currentPositions[idx];
      const curY = this.currentPositions[idx + 1];
      const velX = this.velocities[idx];
      const velY = this.velocities[idx + 1];

      const ax = -k * (curX - targetX) - d * velX;
      const ay = -k * (curY - targetY) - d * velY;

      const newVelX = velX + ax * dt;
      const newVelY = velY + ay * dt;

      const newX = curX + newVelX * dt;
      const newY = curY + newVelY * dt;

      this.velocities[idx] = newVelX;
      this.velocities[idx + 1] = newVelY;
      this.currentPositions[idx] = newX;
      this.currentPositions[idx + 1] = newY;

      // Wave height based on base coordinates (so wave is stable)
      const z =
        this.config.waveHeight *
        this.simplex.noise3d(
          baseX / this.config.noiseScale,
          baseY / this.config.noiseScale,
          time / this.config.animationSpeed,
        );

      // Set displaced position (using spring-smoothed X/Y)
      positions.setXYZ(i, newX, newY, z);

      // Color based on distance from center (using base coords)
      const distance = Math.sqrt(baseX * baseX + baseY * baseY);
      const maxDistance = Math.sqrt(
        (this.config.planeWidth / 2) ** 2 + (this.config.planeHeight / 2) ** 2,
      );

      const alpha =
        1 -
        Math.pow(Math.min(distance / maxDistance, 1), this.config.fadePower);

      const finalR = (1 - alpha) * bgR + alpha * r;
      const finalG = (1 - alpha) * bgG + alpha * g;
      const finalB = (1 - alpha) * bgB + alpha * b;

      colors.setXYZ(i, finalR, finalG, finalB);
    }

    positions.needsUpdate = true;
    colors.needsUpdate = true;
  }

  updateCameraPosition() {
    this.camera.position.set(...this.config.cameraPosition);
    this.camera.rotation.set(
      this.config.cameraRotationX,
      this.config.cameraRotationY,
      this.config.cameraRotationZ,
    );
    // this.camera.lookAt(0, 0, 0);
  }

  onResize() {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  recreateParticles() {
    // Remove old particles and plane
    if (this.particles) this.scene.remove(this.particles);
    if (this.intersectionPlane) this.scene.remove(this.intersectionPlane);

    this.particleGeometry?.dispose();
    this.particles?.material?.dispose();
    this.intersectionPlane?.geometry?.dispose();
    this.intersectionPlane?.material?.dispose();

    // Create new particles with updated geometry
    this.createParticles();
  }

  // --- Mouse handling with raycasting and smooth edge behavior ---

  handleMouseMove(event) {
    const canvas = this.renderer?.domElement;
    if (!canvas || !this.camera || !this.raycaster || !this.intersectionPlane) {
      return;
    }

    const rect = canvas.getBoundingClientRect();
    const { clientX, clientY } = event;

    // Mouse in normalized device coordinates ($-1$ to $1$)
    const xNdc = ((clientX - rect.left) / rect.width) * 2 - 1;
    const yNdc = -((clientY - rect.top) / rect.height) * 2 + 1;

    this.ndcMouse.set(xNdc, yNdc);
    this.raycaster.setFromCamera(this.ndcMouse, this.camera);

    let worldPoint = null;

    // 1) Try intersection with the finite plane geometry
    const intersects = this.raycaster.intersectObject(
      this.intersectionPlane,
      false,
    );
    if (intersects.length) {
      worldPoint = intersects[0].point;
    } else {
      // 2) Fallback: intersect with the infinite plane aligned with the mesh
      this.intersectionPlane.updateWorldMatrix(true, false);

      const worldQuat = new this.THREE.Quaternion();
      this.intersectionPlane.getWorldQuaternion(worldQuat);

      const normal = new this.THREE.Vector3(0, 0, 1).applyQuaternion(worldQuat);
      const planePoint = this.intersectionPlane.getWorldPosition(
        new this.THREE.Vector3(),
      );

      this.interactionPlane.setFromNormalAndCoplanarPoint(normal, planePoint);

      worldPoint = new this.THREE.Vector3();
      if (
        !this.raycaster.ray.intersectPlane(this.interactionPlane, worldPoint)
      ) {
        // No intersection with the infinite plane (e.g., ray parallel)
        this.mouse.isInside = false;
        return;
      }
    }

    // Convert world → local plane coordinates (matches geometry coords)
    const localPoint = this.intersectionPlane.worldToLocal(worldPoint.clone());

    // Clamp to the visible plane rectangle for smooth behavior at edges
    const halfW = this.config.planeWidth / 2;
    const halfH = this.config.planeHeight / 2;

    this.mouse.x = Math.max(-halfW, Math.min(halfW, localPoint.x));
    this.mouse.y = Math.max(-halfH, Math.min(halfH, localPoint.y));

    this.mouse.isInside = true;
  }

  handleMouseLeave() {
    this.mouse.isInside = false;
  }

  dispose() {
    cancelAnimationFrame(this.animationId);
    this.animationId = null;

    this.resizeObserver?.disconnect();

    // Remove mouse listeners from window
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mouseleave', this.onMouseLeave);

    if (this.intersectionPlane) {
      this.scene.remove(this.intersectionPlane);
      this.intersectionPlane.geometry?.dispose();
      this.intersectionPlane.material?.dispose();
    }

    this.renderer?.dispose();
    this.particleGeometry?.dispose();
    this.particles?.material?.dispose();
    this.debugger?.dispose?.();

    if (this.container && this.renderer) {
      this.container.removeChild(this.renderer.domElement);
    }

    this.basePositions = null;
    this.currentPositions = null;
    this.velocities = null;
  }
}
